import React, { useState, FC } from 'react'
import clsx from 'clsx'
import { useActive, useCommands } from '@remirror/react'
import { ChromePicker } from 'react-color'

import { Select } from 'antd'

import { ReactComponent as Bold } from '../../../assets/bold.svg'
import { ReactComponent as Underline } from '../../../assets/underline.svg'
import { ReactComponent as Italic } from '../../../assets/italic.svg'
import { ReactComponent as Strike } from '../../../assets/strike.svg'
import { ReactComponent as Paragraph } from '../../../assets/pilcrow.svg'
import { ReactComponent as H1 } from '../../../assets/h1.svg'
import { ReactComponent as H2 } from '../../../assets/h2.svg'
import { ReactComponent as H3 } from '../../../assets/h3.svg'
import { ReactComponent as HR } from '../../../assets/hr.svg'
import { ReactComponent as Super } from '../../../assets/superscript.svg'
import { ReactComponent as Sub } from '../../../assets/subscript.svg'
import { ReactComponent as LeftAlign } from '../../../assets/leftAlign.svg'
import { ReactComponent as RightAlign } from '../../../assets/rightAlign.svg'
import { ReactComponent as CenterAlign } from '../../../assets/centerAlign.svg'
import { ReactComponent as NumberList } from '../../../assets/numberList.svg'
import { ReactComponent as BulletList } from '../../../assets/bulletList.svg'
import { ReactComponent as LeftIndent } from '../../../assets/leftIndent.svg'
import { ReactComponent as RightIndent } from '../../../assets/rightIndent.svg'
import { ReactComponent as Undo } from '../../../assets/undo.svg'
import { ReactComponent as Redo } from '../../../assets/redo.svg'
import { ReactComponent as TextColorIcon } from '../../../assets/textColor.svg'
import { ReactComponent as BackColorIcon } from '../../../assets/backColor.svg'

import styles from '../_.module.css'

const Line = () => <span className={styles.line} />

const Menu: FC = () => {
  const [showTextPicker, setShowTextPicker] = useState(false)
  const [showBackPicker, setShowBackPicker] = useState(false)
  const [textColor, setTextColor] = useState('#000000')
  const [backColor, setBackColor] = useState('#000000')

  const commands = useCommands()
  const active = useActive()
  const headingListOptions = [
    {
      value: 1,
      label: <H1 />
    },
    {
      value: 2,
      label: <H2 />
    },
    {
      value: 3,
      label: <H3 />
    },
    {
      value: 'hr',
      label: <HR />
    },
    {
      value: 'paragraph',
      label: <Paragraph />
    }
  ]
  const alignListOptions = [
    {
      value: 'left',
      label: <LeftAlign />
    },
    {
      value: 'right',
      label: <RightAlign />
    },
    {
      value: 'center',
      label: <CenterAlign />
    }
  ]
  const listsOptions = [
    {
      value: 'order',
      label: (
        <div
          className={clsx(styles.selectOption, {
            [styles.activeButton]: active.orderedList()
          })}
        >
          <NumberList />
        </div>
      )
    },
    {
      value: 'bullet',
      label: (
        <div
          className={clsx(styles.selectOption, {
            [styles.activeButton]: active.bulletList()
          })}
        >
          <BulletList />
        </div>
      )
    }
  ]
  const indentOptions = [
    {
      value: 'increaseIndent',
      label: <LeftIndent />
    },
    {
      value: 'decreaseIndent',
      label: <RightIndent />
    }
  ]

  const updateHeading = (val: string | number) => {
    if (!isNaN(Number(val))) {
      commands.toggleHeading({ level: Number(val) })
    } else if (val === 'paragraph') {
      commands.insertParagraph(' ')
    } else {
      commands.insertHorizontalRule()
    }
    commands.focus()
  }
  const updateListCommand = (val: string) => {
    switch (val) {
      case 'left': {
        commands.leftAlign()
        break
      }
      case 'right': {
        commands.rightAlign()
        break
      }
      case 'center': {
        commands.centerAlign()
        break
      }
      case 'bullet': {
        commands.toggleBulletList()
        break
      }
      case 'order': {
        commands.toggleOrderedList()
        break
      }
      case 'decreaseIndent': {
        commands.decreaseIndent()
        break
      }
      case 'increaseIndent': {
        commands.increaseIndent()
        break
      }
      default: {
        return null
      }
    }
    commands.focus()
  }
  const updateTextColor = () => {
    commands.setTextColor(textColor)
    setShowTextPicker(false)
  }
  const updateBackColor = () => {
    commands.setTextHighlight(backColor)
    setShowBackPicker(false)
  }

  return (
    <div className={clsx(styles.menuArea, styles.customScrollBar)}>
      <div className={styles.menuWrap}>
        <Select
          onChange={(e: any): void => updateHeading(e)}
          className={styles.selectWrap}
          options={headingListOptions}
          placeholder={<H1 />}
          size="small"
        />
        <button
          onClick={() => {
            commands.toggleBold()
            commands.focus()
          }}
          className={clsx(styles.buttonWrap, {
            [styles.activeButton]: active.bold()
          })}
        >
          <Bold />
        </button>
        <button
          onClick={() => {
            commands.toggleItalic()
            commands.focus()
          }}
          className={clsx(styles.buttonWrap, {
            [styles.activeButton]: active.italic()
          })}
        >
          <Italic />
        </button>

        <button
          onClick={() => {
            commands.toggleUnderline()
            commands.focus()
          }}
          className={clsx(styles.buttonWrap, {
            [styles.activeButton]: active.underline()
          })}
        >
          <Underline />
        </button>
        <button
          onClick={() => {
            commands.toggleStrike()
            commands.focus()
          }}
          className={clsx(styles.buttonWrap, {
            [styles.activeButton]: active.strike()
          })}
        >
          <Strike />
        </button>

        <Line />
        <div className={styles.colorPickerWrap}>
          <button
            onClick={() => {
              showBackPicker && setShowBackPicker(false)
              setShowTextPicker(true)
            }}
            className={styles.buttonWrap}
          >
            <TextColorIcon />
          </button>
          {showTextPicker && (
            <div className={styles.colorPicker}>
              <div
                onClick={() => {
                  setShowTextPicker(false)
                  updateTextColor()
                }}
                className={styles.backdrop}
                role="none"
              />
              <ChromePicker
                className={styles.colorFocused}
                disableAlpha={true}
                color={textColor}
                onChangeComplete={updatedColor =>
                  setTextColor(updatedColor.hex)
                }
              />
            </div>
          )}
        </div>
        <div className={styles.colorPickerWrap}>
          <button
            onClick={() => {
              showTextPicker && setShowTextPicker(false)
              setShowBackPicker(true)
            }}
            className={styles.buttonWrap}
          >
            <BackColorIcon />
          </button>
          {showBackPicker && (
            <div className={styles.colorPicker}>
              <div
                onClick={() => {
                  setShowBackPicker(false)
                  updateBackColor()
                }}
                className={styles.backdrop}
                role="none"
              />
              <ChromePicker
                color={backColor}
                disableAlpha={true}
                className={styles.colorFocused}
                onChangeComplete={updatedColor =>
                  setBackColor(updatedColor.hex)
                }
              />
            </div>
          )}
        </div>

        <Line />

        <button
          onClick={() => {
            commands.toggleSubscript()
            commands.focus()
          }}
          className={clsx(styles.buttonWrap, {
            [styles.activeButton]: active.sub()
          })}
        >
          <Sub />
        </button>

        <button
          onClick={() => {
            commands.toggleSuperscript()
            commands.focus()
          }}
          className={clsx(styles.buttonWrap, {
            [styles.activeButton]: active.sup()
          })}
        >
          <Super />
        </button>

        <Line />

        <Select
          onChange={(e: any) => updateListCommand(e)}
          className={styles.selectWrap}
          options={alignListOptions}
          placeholder={<LeftAlign />}
          size="small"
        />
        <Select
          onChange={(e: any) => updateListCommand(e)}
          className={styles.selectWrap}
          options={listsOptions}
          placeholder={<NumberList />}
          size="small"
        />
        <Select
          onChange={(e: any) => updateListCommand(e)}
          className={styles.selectWrap}
          options={indentOptions}
          placeholder={<LeftIndent />}
          size="small"
        />

        <Line />

        <button
          onClick={() => {
            commands.undo()
            commands.focus()
          }}
          disabled={!commands.undo.enabled()}
          className={clsx(styles.buttonWrap, {
            [styles.disabledButton]: !commands.undo.enabled()
          })}
        >
          <Undo />
        </button>
        <button
          onClick={() => {
            commands.redo()
            commands.focus()
          }}
          disabled={!commands.redo.enabled()}
          className={clsx(styles.buttonWrap, {
            [styles.disabledButton]: !commands.redo.enabled()
          })}
        >
          <Redo />
        </button>
      </div>
    </div>
  )
}

export default Menu
